import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBlogs } from "@/lib/api";
import { ArrowUpRight, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function BlogSection({ interest }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchBlogs(interest)
      .then((b) => active && setBlogs(b))
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [interest]);

  const featured = blogs[0];
  const rest = blogs.slice(1, 5);

  return (
    <section
      id="journal"
      className="bg-[var(--vp-bg)] py-24 md:py-32 px-6 md:px-12"
      data-testid="blog-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <span className="vp-label block mb-4">THE JOURNAL</span>
            <h2 className="font-serif-vp text-4xl md:text-5xl lg:text-6xl text-[var(--vp-primary)] leading-[1.05]">
              {interest ? (
                <>
                  Curated for your interest in<br />
                  <em className="italic text-[var(--vp-secondary)]">{interest}</em>.
                </>
              ) : (
                <>Field notes from<br />our curators.</>
              )}
            </h2>
          </div>
          <p className="text-[var(--vp-muted)] max-w-sm font-sans-vp">
            Slow essays, concierge guides, and city letters — written only by people who have been.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-12 gap-8">
            <Skeleton className="md:col-span-7 h-[520px]" />
            <div className="md:col-span-5 grid grid-cols-1 gap-5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[110px]" />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-12 gap-8">
            {featured && <FeaturedPost post={featured} />}
            <div className="md:col-span-5 grid grid-cols-1 gap-5">
              {rest.map((p) => (
                <SidePost key={p.id} post={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturedPost({ post }) {
  return (
    <Link
      to={`/blog/${post.id}`}
      className="md:col-span-7 vp-card relative overflow-hidden group cursor-pointer h-[520px] block"
      data-testid={`blog-featured-${post.id}`}
    >
      <img
        src={post.image}
        alt={post.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
      <div className="absolute top-6 left-6">
        <span className="bg-[var(--vp-secondary)] text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full font-medium">
          Editor’s pick
        </span>
      </div>
      <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-end text-white">
        <div className="flex items-center gap-3 mb-4">
          <span className="vp-label text-white/95">{post.category}</span>
          <span className="text-white/60">·</span>
          <span className="flex items-center gap-1.5 text-xs text-white/80 tracking-wider">
            <Clock className="w-3 h-3" />
            {post.read_time}
          </span>
        </div>
        <h3 className="font-serif-vp text-3xl md:text-5xl leading-[1.05] mb-4 max-w-2xl">
          {post.title}
        </h3>
        <p className="text-white/90 max-w-xl font-sans-vp text-base leading-relaxed mb-6">
          {post.excerpt}
        </p>
        <div className="flex items-center gap-2 text-white text-xs tracking-[0.18em] uppercase font-medium group-hover:gap-4 transition-all">
          Read the essay <ArrowUpRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

function SidePost({ post }) {
  return (
    <Link
      to={`/blog/${post.id}`}
      className="group cursor-pointer flex gap-4 p-4 rounded-xl bg-white border border-[var(--vp-border)] hover:border-[var(--vp-primary)] hover:-translate-y-0.5 transition-all"
      data-testid={`blog-side-${post.id}`}
    >
      <div className="w-28 h-24 rounded-lg overflow-hidden shrink-0">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="vp-label text-[10px]">{post.category}</span>
            <span className="text-[var(--vp-muted)]">·</span>
            <span className="text-[11px] text-[var(--vp-muted)]">{post.read_time}</span>
          </div>
          <h4 className="font-serif-vp text-lg text-[var(--vp-primary)] leading-snug line-clamp-2 group-hover:text-[var(--vp-secondary)] transition-colors">
            {post.title}
          </h4>
        </div>
        <p className="text-xs text-[var(--vp-muted)] line-clamp-2 font-sans-vp">
          {post.excerpt}
        </p>
      </div>
      <ArrowUpRight
        className="w-4 h-4 text-[var(--vp-muted)] shrink-0 opacity-0 group-hover:opacity-100 transition"
        strokeWidth={1.5}
      />
    </Link>
  );
}
